import { forwardRef } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';

import { releasePhaseMessages } from './messages.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import { Chip } from '../chip/Chip.js';

const COMPONENT_NAME = 'ReleasePhaseIndicator';

/**
 * The release phase to display. Determines the chip label and color.
 * @public
 */
export type ReleasePhase = 'preview' | 'new' | 'deprecated' | 'early-access';

/**
 * Accepted properties for ReleasePhaseIndicator
 * @public
 */
export interface ReleasePhaseIndicatorProps
  extends DOMProps,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * The release phase to display. Determines the chip label and color.
   */
  phase: ReleasePhase;
}

const phaseColorMap: Record<ReleasePhase, 'primary' | 'warning'> = {
  preview: 'primary',
  new: 'primary',
  deprecated: 'warning',
  'early-access': 'primary',
};

const phaseLabelMap: Record<ReleasePhase, MessageDescriptor> = {
  preview: releasePhaseMessages.preview,
  new: releasePhaseMessages.new,
  deprecated: releasePhaseMessages.deprecated,
  'early-access': releasePhaseMessages.earlyAccess,
};

/**
 * Use the `ReleasePhaseIndicator` to communicate the release phase
 * of a feature (Preview, New, Early Access, Deprecated).
 * @public
 */
export const ReleasePhaseIndicator = forwardRef<
  HTMLDivElement,
  ReleasePhaseIndicatorProps
>((props, forwardedRef) => {
  const {
    phase,
    id,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const intl = useIntl();

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  return (
    <Chip
      id={id}
      ref={forwardedRef}
      size="condensed"
      variant="emphasized"
      color={phaseColorMap[phase]}
      className={consumerClassName}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      {intl.formatMessage(phaseLabelMap[phase])}
    </Chip>
  );
});

(
  ReleasePhaseIndicator as typeof ReleasePhaseIndicator & {
    displayName: string;
  }
).displayName = 'ReleasePhaseIndicator';
